import React, {useEffect, useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css as emotionCss} from "@emotion/react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Col, WideContent} from "@pg-design/grid-module";
import {mb, mt, onDesktop} from "@pg-design/helpers-css";
import {BrandSearchIcon, MapIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible, usePrevious} from "@pg-mono/hooks";
import {debounce, omit} from "@pg-mono/nodash";
import {parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {IApiRange} from "@pg-mono/rp-api-routes";
import {LISTING_VENDOR_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {PRICE_VARIANT_CITIES_SLUGS} from "@pg-rp/navigation";

import {IRPStore} from "../../../../../app/rp_reducer";
import {ColumnList} from "../../../../../atoms/ColumnList";
import {ColumnListItem} from "../../../../../atoms/ColumnListItem";
import {InfoPanel} from "../../../../../atoms/InfoPanel";
import {Country} from "../../../../../region/types/Country";
import {useSearchForm} from "../../../../../search/hooks/use_search_form";
import {SearchFormValuesType} from "../../../../../search/types/SearchFormValuesType";
import {TdaPortal} from "../../../../../smart_ad_server/components/TdaPortal";
import {SmartAdServerPlacement} from "../../../../../smart_ad_server/SmartAdServerPlacement";
import {offersVisibleHit} from "../../../../../tracking/algolytics_hits/offers_visible_hit";
import {trackAmplitudeButtonClick} from "../../../../../tracking/amplitude/track_amplitude_button_click";
import {OfferBoxWithOfferDetailsAction} from "../../../../atoms/OfferBoxWithOfferDetailsAction";
import {BudgetFilterBox} from "../../../../components/BudgetFilterBox";
import {BudgetVariantRadioBox} from "../../../../components/BudgetVariantRadioBox";
import {RoomsFilterBox} from "../../../../components/RoomsFilterBox";
import {getAvailableFlatPriceVariants} from "../../../../helpers/get_flat_price_variant_links";
import {SmartAdSASTypes} from "../../../../helpers/smart_add_sas_types";
import {useOffersViewsUrl} from "../../../../hooks/use_offers_views_url";
import {hitGTMOfferViewItemList} from "../../../../tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../../../../tracking/offer_box_gtm_events";
import {IOfferListOffer} from "../../../../types/IOfferListOffer";
import {OfferType} from "../../../../types/OfferType";
import {getOfferCardAddress, IOfferCardAddressContext} from "../../../../utils/get_offer_card_address";
import {ISelectedRegion} from "../../../actions/offer_list_selected_data_actions";
import {OFFER_LIST_PAGE_SIZE} from "../../../constants/offer_list";
import {getListingLayoutSearchKey} from "../../../helpers/get_listing_layout_search_key";
import {getOfferListingAmplitudeItemListContext} from "../../../helpers/get_offer_listing_amplitude_item_list_context";
import {isPromotionsListingQuery} from "../../../helpers/map_offer_list_promotions_query_param";
import {shouldShowOfferListingPromotionTabs} from "../../../helpers/should_show_offer_listing_promotion_tabs";
import {getCountryId} from "../../../helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {useGetOfferListingAllOffers} from "../../../hooks/use_get_offer_listing_all_offers";
import {disableDynamicPositionProps, useListDynamicPositions} from "../../../hooks/use_list_dynamic_positions";
import {useOfferListingAmplitudeViewItemListTracking} from "../../../hooks/use_offer_listing_amplitude_view_item_list_tracking";
import {usePageLinkBuilder} from "../../../hooks/usePageLinkBuilder";
import {IOfferListListStore} from "../../../reducers/desktop_offer_list_list_reducer";
import {reservedGridRowStyle} from "../../../styles/reserved_grid_row_style";
import {OfferListSubType} from "../../../types/OfferListSubType";
import {hasPriceFilter} from "../../../utils/has_price_filter";
import {DefaultOfferListingModal} from "../../DefaultOfferListingModal";
import {NextPageBox} from "../../NextPageBox";
import {OfferListingPromotionTabs} from "../../OfferListingPromotionTabs";
import {OfferListSort} from "../../OfferListSort";
import {GetHomeLinkingBox} from "../GetHomeLinkingBox";
import {NotificationsListBox} from "../NotificationsListBox";
import {OfferListHeader} from "../OfferListHeader";
import {OfferListMidBanner} from "../OfferListMidBanner";
import {PromotionsListingTile} from "../PromotionsListingTile";
import {UnactiveOfferList} from "./UnactiveOfferList";

interface IProps {
    list: IOfferListListStore;
    metaDataTitle: string;
    selectedRegion: ISelectedRegion[];
    offerListSubType?: OfferListSubType;
    isSidebarReserved?: boolean;
    isInitialOfferListFetchDone: boolean;
    addressContext: IOfferCardAddressContext;
}

export enum CreationListingPosition {
    SURPRISE_LONG = 17,
    SURPRISE_SHORT = 10
}

export const DefaultOfferListing = (props: IProps) => {
    const {addressContext, list, metaDataTitle, selectedRegion, offerListSubType, isSidebarReserved, isInitialOfferListFetchDone} = props;
    const {
        count,
        offers,
        privileged_offers,
        page,
        unavailable_offers,
        unavailable_offers_count,
        requestState,
        latestQuery,
        page_size,
        allOffers,
        propertiesCount,
        listingContextPromotionalOffersCount,
        listingContextPromotionalOffersCountRequestState
    } = list;
    const searchFormValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;
    const standardMetaData = useSelector((state: IRPStore) => state.metaData.standardMetaData);
    const searchFormRooms = searchFormValues.rooms as IApiRange<number | "">;
    const priceFilterUsed = hasPriceFilter(searchFormValues);
    const allOffersCount = priceFilterUsed ? allOffers.filter((offer) => offer.has_required_prices === true).length : count;
    const buildPageLink = usePageLinkBuilder();
    const {isMobile} = useUserDevice();
    const location = useLocation();
    const routeQuery = parseSearch(location.search);
    /**
     * `location.search` also carries the routed-modal param, which changes on every "Zobacz mieszkania"
     * click without changing what the listing renders. Keying the render memo off the stripped search
     * keeps opening the modal from re-rendering the whole list
     */
    const layoutSearchKey = getListingLayoutSearchKey(location.search);
    // built once instead of per tile, so tiles without required prices keep a stable prop identity
    const latestQueryWithoutPrice = useMemo(() => omit(latestQuery, ["price_0", "price_1", "instalment"]), [latestQuery]);
    const prevRequestState = usePrevious(requestState, RequestState.None);
    const offerType = !!latestQuery.type ? parseInt(latestQuery.type) : undefined;
    const currentCountry = getCountryId(latestQuery.country);
    const isAbroadListing = currentCountry && currentCountry !== Country.POLAND;
    const listingPageSize = page_size || OFFER_LIST_PAGE_SIZE;
    const lastPage = allOffersCount ? Math.ceil(allOffersCount / listingPageSize) : 1;
    const isPromotionsTab = isPromotionsListingQuery(routeQuery);
    const showPromotionTabs = shouldShowOfferListingPromotionTabs({
        latestQuery,
        routeQuery,
        listingContextPromotionalOffersCount,
        listingContextPromotionalOffersCountRequestState
    });
    const listingListKey = isPromotionsTab ? "promotions" : "all";

    const offerRequestReady = prevRequestState === RequestState.Waiting && requestState === RequestState.Success;
    const isLastPage = page === lastPage;
    const lastCombinedPage = Math.ceil((count + unavailable_offers_count) / listingPageSize) || 1;
    // latestQuery.page is the URL page (set on fetchOfferListTypes.start, i.e. immediately on navigation).
    // For pure-unavailable pages the store's `page` field holds activeLastPage (not the URL page),
    // so comparing against that would always miss the last combined page.
    const urlPage = latestQuery.page ? parseInt(String(latestQuery.page), 10) : 1;
    const isLastCombinedPage = urlPage === lastCombinedPage;
    // True for transition and pure-unavailable pages — use stable computed values, not unavailable_offers.length
    // (which can be transiently stale from a previous combined page during loading)
    const activeLastPageFromCount = count > 0 ? Math.ceil(count / listingPageSize) : 1;
    const isOnCombinedOrUnavailablePage = page >= activeLastPageFromCount && unavailable_offers_count > 0;
    const unavailableOffersIds = unavailable_offers.map((offer) => offer.id).join(",");

    const {mainOffers, additionalOffers, mainOffersCount} = useGetOfferListingAllOffers({
        offersOnPage: offers,
        allOffers,
        latestQuery,
        searchFormValues,
        privilegedOffers: privileged_offers,
        isLastPage
    });

    const offersToRender = [...mainOffers, ...additionalOffers];

    const shouldShowRoomsFilterBox = mainOffers.length !== 0 && count >= 24 && searchFormRooms.upper === "" && searchFormRooms.lower === "";
    const shouldShowBudgetFilterBox = mainOffers.length !== 0 && count >= 24 && !searchFormValues.instalment && !searchFormValues.price.upper;

    const seenOffers = useRef<number[]>([]);
    const roomsFilterBoxPosition = 4;
    const budgetFilterBoxPosition = shouldShowRoomsFilterBox ? roomsFilterBoxPosition + 3 : roomsFilterBoxPosition;
    const priceDividerOfferIndex = offers.findIndex((el) => el.has_required_prices !== true) || 0;
    const dividedOffersCount = priceFilterUsed ? offers.slice(priceDividerOfferIndex, offers.length).length : 0;

    const {mapUrl} = useOffersViewsUrl();

    const debouncedOffersVisibleHit = debounce(() => {
        offersVisibleHit(seenOffers.current);

        seenOffers.current = [];
    }, 500);

    const offersToRenderIds = useMemo(() => [...offersToRender.map(({id}) => id.toString())], [offersToRender]);
    const offerToRenderIndexes = useMemo(() => {
        const indexes: {[key: string]: number} = {};
        offersToRender.forEach((offer, index) => {
            indexes[offer.id] = index;
        });
        return indexes;
    }, [offersToRender]);

    useElementWasVisible({
        ids: offersToRenderIds,
        indexes: offerToRenderIndexes,
        onElementVisible: (id, _, index) => {
            seenOffers.current = [...seenOffers.current, parseInt(id)];
            index !== undefined && offerBoxTracking.gtm.investTileView(parseInt(id), index);
            debouncedOffersVisibleHit();
        },
        once: true
    });

    const offersListLayoutKey = useMemo(
        () => `${listingListKey}|${layoutSearchKey}|${offersToRender.map((offer, index) => `${offer.id}-${index}`).join(",")}`,
        [listingListKey, layoutSearchKey, offersToRender]
    );

    const {wrapperProps, initializeDynamicPosition, getDynamicPositionProps} = useListDynamicPositions<HTMLDivElement>([offersListLayoutKey]);
    const {
        wrapperProps: unavailableWrapperProps,
        initializeDynamicPosition: initializeUnavailableDynamicPosition,
        getDynamicPositionProps: getUnavailableDynamicPositionProps
    } = useListDynamicPositions<HTMLDivElement>([unavailableOffersIds]);

    // Commented out due to RP-217 requirements
    // const midListingReels = useMemo(() => {
    //     return <ListingReels selectedRegions={props.selectedRegion} className="listing-reels" />;
    // }, [props.selectedRegion]);

    const {openLocationSearch} = useSearchForm();
    const handleHeaderClick = () => {
        openLocationSearch();
    };

    const prevMetaDataTitle = useRef<string | null>(null);

    const listingTitle = metaDataTitle.split(" - ")[0];
    const amplitudeItemListContext = useMemo(
        () =>
            getOfferListingAmplitudeItemListContext({
                listingTitle,
                isPromotionsTab,
                hasPromotionTabs: showPromotionTabs,
                latestQuery
            }),
        [listingTitle, isPromotionsTab, showPromotionTabs, latestQuery]
    );

    useOfferListingAmplitudeViewItemListTracking({
        offers: offersToRender,
        amplitudeItemListContext,
        routeQuery,
        latestQuery,
        requestState,
        isInitialOfferListFetchDone,
        metaDataRevision: standardMetaData
    });

    const handleMapButtonClick = () => {
        trackAmplitudeButtonClick({
            buttonName: "view_map",
            buttonCategory: "listing_page",
            value: "see_on_map",
            itemId: null,
            buttonLink: null,
            itemListId: amplitudeItemListContext.itemListId
        });
    };

    // memoized so that the memo() on OfferBox actually holds - a fresh object here re-rendered every tile
    const trackingMeta = useMemo(
        () => ({
            listId: "listing",
            listName: listingTitle,
            source: "listing" as const,
            tileEvents: {
                imageClick: offerBoxTracking.gtm.investTileImageClick,
                buttonClick: offerBoxTracking.gtm.investTileButtonClick,
                linkClick: offerBoxTracking.gtm.investTileLinkClick,
                logoClick: offerBoxTracking.gtm.investTileLogoClick
            }
        }),
        [listingTitle]
    );

    useEffect(() => {
        if (requestState === RequestState.Success && prevMetaDataTitle.current !== metaDataTitle) {
            prevMetaDataTitle.current = metaDataTitle;
            hitGTMOfferViewItemList({
                offers: offersToRender,
                listId: trackingMeta.listId,
                listName: trackingMeta.listName
            });
        }
    }, [requestState, offersToRenderIds.join(","), metaDataTitle]);

    const renderListSection = useMemo(() => {
        let hasPriceDivider = false;

        const renderDivider = (has_required_prices: boolean | null) => {
            const shouldHaveDivider = !hasPriceDivider && has_required_prices === false;
            if (shouldHaveDivider) {
                hasPriceDivider = true;
            }
            return (
                shouldHaveDivider && (
                    <Col sm={4} md={12} lg={16} className={dividerStyles}>
                        <InfoPanel
                            icon={BrandSearchIcon}
                            heading="Znaleźliśmy także inne oferty, które mogą nie spełniać Twoich oczekiwań cenowych."
                            subHeading="Szczegółowych informacji udzielają deweloperzy"
                        />
                    </Col>
                )
            );
        };

        const getRegion = () => {
            const regionId = latestQuery.region && latestQuery.region?.length > 0 ? +latestQuery.region[latestQuery.region.length - 1] : undefined;
            const selectedRegionData = selectedRegion.find((region) => region.id === regionId);

            if (regionId && selectedRegion.length > 0) {
                return {
                    id: regionId,
                    name: selectedRegionData?.name,
                    slug: selectedRegionData?.slug,
                    parentSlug: selectedRegionData?.parent?.slug,
                    type: selectedRegionData?.type,
                    stats: selectedRegionData?.stats
                };
            }
            return {
                id: regionId,
                name: undefined,
                slug: undefined,
                parentSlug: undefined,
                type: undefined,
                stats: undefined
            };
        };
        const region = getRegion();

        const budgetPriceVariants =
            offerType === OfferType.FLAT && !!region.slug && PRICE_VARIANT_CITIES_SLUGS.includes(region.slug)
                ? getAvailableFlatPriceVariants(region.stats)
                : [];
        const showBudgetVariants = budgetPriceVariants.length >= 3;

        let suffix = 1;
        initializeDynamicPosition();
        initializeUnavailableDynamicPosition();
        return (
            <>
                {!isMobile && (!currentCountry || currentCountry === Country.POLAND) && (
                    <TdaPortal>
                        <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />
                    </TdaPortal>
                )}
                <DefaultOfferListingModal />

                <OfferListHeader
                    offerCount={allOffers.length > 0 ? mainOffersCount : count - dividedOffersCount}
                    propertiesCount={propertiesCount}
                    selectedRegion={selectedRegion}
                    latestQuery={latestQuery}
                    offerListSubType={offerListSubType}
                    onHeaderClick={handleHeaderClick}
                />

                {/*<WideContent css={[display("none"), onDesktop(display("block"), mb(4), w100)]}>*/}
                {/*    <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} fetchPriority="high" preventGtmOnMobile />*/}
                {/*</WideContent>*/}

                <WideContent>
                    <div className={cx(offerListControlsStyle, showPromotionTabs ? offerListControlsSpaceBetweenStyle : offerListControlsFlexEndStyle)}>
                        {/* @deprecated Comment code releated with displaying "Mieszkania i domy" tab due to RP-217 requirements
                        this can be delted entirely in future if we will keep this approach */}
                        {/* <ListingTypeTabs
                            filters={searchFormValues}
                            offerType={offerType}
                            currentListingType={ListingType.OFFERS}
                            additionalParams={{sort: props.list.latestQuery.sort}}
                        /> */}

                        {showPromotionTabs && <OfferListingPromotionTabs amplitudeItemListContext={amplitudeItemListContext} />}

                        <div className={mapButtonsWrapperStyle}>
                            <Button
                                className={mapButtonStyle}
                                variant="filled_secondary"
                                size="small"
                                href={mapUrl}
                                iconLeft={MapIcon}
                                onClick={handleMapButtonClick}
                            >
                                zobacz na mapie
                            </Button>

                            <OfferListSort type="select" name="offer-sort" latestQuerySort={latestQuery.sort} css={offerListSortWrapperStyle} />
                        </div>
                    </div>
                </WideContent>
                <ColumnList
                    {...wrapperProps}
                    className={isSidebarReserved ? listDynamicRowOrderReservedSidebarStyle : listDynamicRowOrderStyle}
                    rowClassName={isSidebarReserved ? reservedGridRowStyle : undefined}
                >
                    {offersToRender.map((offer: IOfferListOffer, idx: number) => {
                        const position = idx + 1;
                        const isShortListCase = offersToRender.length < 3 && position === offersToRender.length; // list is short, so detect the last element
                        const isFifthLastIndex = position === offersToRender.length - 5;
                        const isLastPosition = position === offersToRender.length;

                        const surpriseAdPosition =
                            offersToRender.length >= CreationListingPosition.SURPRISE_LONG
                                ? CreationListingPosition.SURPRISE_LONG
                                : CreationListingPosition.SURPRISE_SHORT;

                        return (
                            <React.Fragment key={`${offer.id}-${idx}`}>
                                {renderDivider(offer.has_required_prices)}
                                <ColumnListItem data-testid={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT} dynamicProps={getDynamicPositionProps()}>
                                    <OfferBoxWithOfferDetailsAction
                                        offer={offer}
                                        latestQuery={offer.has_required_prices === false ? latestQueryWithoutPrice : latestQuery}
                                        index={idx}
                                        trackingMeta={trackingMeta}
                                        displayAddress={getOfferCardAddress(offer, addressContext)}
                                        amplitudeItemListContext={amplitudeItemListContext}
                                        dataTestId={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}
                                    />
                                </ColumnListItem>
                                {/*{position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} />}*/}
                                {/* Hidden due to RP-217 requirements */}
                                {/* {position === 2 && midListingReels} */}
                                {position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />}
                                {(isShortListCase || position === 3) && <SmartAdServerPlacement adFormat={SmartAdSASTypes.OFFER_OF_THE_MONTH} />}
                                {(isShortListCase || position === 3 || position === 7 || position === 11) && (
                                    <SmartAdServerPlacement adFormat={SmartAdSASTypes.MONO_NATIVE_ADD} suffix={suffix++} />
                                )}

                                {isFifthLastIndex && <SmartAdServerPlacement adFormat={SmartAdSASTypes.IMAGE} />}
                                {position === surpriseAdPosition && (
                                    <SmartAdServerPlacement
                                        adFormat={SmartAdSASTypes.SURPRISE}
                                        referenceElementId={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT}
                                    />
                                )}
                                {position === 5 && <OfferListMidBanner />}

                                {position === 6 && (
                                    <PromotionsListingTile
                                        selectedRegion={selectedRegion}
                                        getDynamicPositionProps={getDynamicPositionProps}
                                        listingPage={page}
                                        tilePosition={position}
                                        amplitudeItemListContext={amplitudeItemListContext}
                                    />
                                )}

                                {isLastPosition && !isOnCombinedOrUnavailablePage && (
                                    <>
                                        <ColumnListItem {...getDynamicPositionProps()}>
                                            <NextPageBox
                                                isLastPage={isLastCombinedPage}
                                                offerTypes={offerType ? [offerType] : [OfferType.FLAT, OfferType.HOUSE]}
                                                nextPageLink={buildPageLink(page + 1)}
                                            />
                                        </ColumnListItem>
                                        {isLastCombinedPage && region.name && (
                                            <ColumnListItem {...getDynamicPositionProps()}>
                                                <NotificationsListBox regionName={region.name} />
                                            </ColumnListItem>
                                        )}
                                        {isLastCombinedPage && offerType !== OfferType.COMMERCIAL && !isAbroadListing && (
                                            <ColumnListItem {...getDynamicPositionProps()}>
                                                <GetHomeLinkingBox offerType={offerType} region={region} />
                                            </ColumnListItem>
                                        )}
                                    </>
                                )}
                                {shouldShowRoomsFilterBox && position === roomsFilterBoxPosition && (
                                    <ColumnListItem
                                        dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}
                                        className="rp-filters-tile"
                                    >
                                        <RoomsFilterBox />
                                    </ColumnListItem>
                                )}
                                {shouldShowBudgetFilterBox && position === (isMobile ? budgetFilterBoxPosition - 1 : budgetFilterBoxPosition) && (
                                    <ColumnListItem dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}>
                                        {showBudgetVariants ? <BudgetVariantRadioBox priceVariants={budgetPriceVariants} /> : <BudgetFilterBox />}
                                    </ColumnListItem>
                                )}
                            </React.Fragment>
                        );
                    })}
                </ColumnList>

                {unavailable_offers.length > 0 && (
                    <>
                        <WideContent>
                            <Text variant="mini_header" as="p" css={[offersToRender.length > 0 && mt(6), mb(3)]}>
                                Oferty nieaktywne
                            </Text>
                        </WideContent>
                        <ColumnList
                            {...unavailableWrapperProps}
                            className={isSidebarReserved ? listDynamicRowOrderReservedSidebarStyle : listDynamicRowOrderStyle}
                            rowClassName={isSidebarReserved ? reservedGridRowStyle : undefined}
                        >
                            <UnactiveOfferList offers={unavailable_offers} addressContext={addressContext} />

                            <ColumnListItem {...getUnavailableDynamicPositionProps()}>
                                <NextPageBox
                                    isLastPage={isLastCombinedPage}
                                    offerTypes={offerType ? [offerType] : [OfferType.FLAT, OfferType.HOUSE]}
                                    nextPageLink={buildPageLink(page + 1)}
                                />
                            </ColumnListItem>
                            {isLastCombinedPage && region.name && (
                                <ColumnListItem {...getUnavailableDynamicPositionProps()}>
                                    <NotificationsListBox regionName={region.name} />
                                </ColumnListItem>
                            )}
                            {isLastCombinedPage && offerType !== OfferType.COMMERCIAL && !isAbroadListing && (
                                <ColumnListItem {...getUnavailableDynamicPositionProps()}>
                                    <GetHomeLinkingBox offerType={offerType} region={region} />
                                </ColumnListItem>
                            )}
                        </ColumnList>
                    </>
                )}
            </>
        );
    }, [
        offersToRenderIds,
        offerRequestReady,
        metaDataTitle,
        latestQuery,
        allOffers.length,
        listingListKey,
        layoutSearchKey,
        showPromotionTabs,
        amplitudeItemListContext,
        isSidebarReserved,
        unavailable_offers_count,
        unavailableOffersIds,
        isLastCombinedPage,
        isOnCombinedOrUnavailablePage,
        addressContext
    ]);

    return renderListSection;
};

//  Styles
const dividerStyles = css({
    py: "3rem",
    px: 0
});

const offerListControlsStyle = css({
    display: "flex",
    alignItems: "center",
    pb: 2,
    mt: 2,
    md: {
        pb: 3,
        mt: 0
    },
    "& > div": {
        pb: 0
    }
});

const offerListControlsSpaceBetweenStyle = css({
    justifyContent: "space-between"
});

const offerListControlsFlexEndStyle = css({
    justifyContent: "flex-end"
});

const mapButtonsWrapperStyle = css({
    display: "flex",
    gap: "1rem",
    alignItems: "center"
});

const mapButtonStyle = css({
    display: "none",
    md: {
        display: "inherit"
    }
});

const offerListSortWrapperStyle = emotionCss`
    display: none;
    padding-bottom: 0;

    ${onDesktop(emotionCss`
        display: block;
        flex: 0 0 16rem;
    `)};
`;

const listDynamicRowOrderRawStyle = css.raw({
    mdDown: {
        '& [data-pos="0"], & [data-pos="1"]': {
            order: -4
        },
        "& .listing-reels": {
            order: -3
        },
        '& [data-pos="2"], & [data-pos="3"]': {
            order: -2
        },
        "& .rp-filters-tile": {
            order: -1
        }
    },
    md: {
        '& [data-pos="0"], & [data-pos="1"], & [data-pos="2"], & [data-pos="3"], & [data-pos="4"], & [data-pos="5"]': {
            order: -4
        },
        "& .listing-reels": {
            order: -3
        },
        '& [data-pos="6"], & [data-pos="7"], & [data-pos="8"], & [data-pos="9"], & [data-pos="10"], & [data-pos="11"]': {
            order: -2
        },
        "& .listing-banner": {
            order: -1
        }
    }
});

const listDynamicRowOrderReservedSidebarStyle = css({
    ...listDynamicRowOrderRawStyle
});

const listDynamicRowOrderStyle = css({
    ...listDynamicRowOrderRawStyle,
    lg: {
        '& [data-pos="0"], & [data-pos="1"], & [data-pos="2"], & [data-pos="3"], & [data-pos="4"], & [data-pos="5"], & [data-pos="6"], & [data-pos="7"]': {
            order: -4
        },
        '& [data-pos="8"], & [data-pos="9"], & [data-pos="10"], & [data-pos="11"], & [data-pos="12"], & [data-pos="13"], & [data-pos="14"], & [data-pos="15"]':
            {
                order: -2
            }
    }
});
import React, {useEffect, useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css as emotionCss} from "@emotion/react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Col, WideContent} from "@pg-design/grid-module";
import {mb, mt, onDesktop} from "@pg-design/helpers-css";
import {BrandSearchIcon, MapIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible, usePrevious} from "@pg-mono/hooks";
import {debounce, omit} from "@pg-mono/nodash";
import {parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {IApiRange} from "@pg-mono/rp-api-routes";
import {LISTING_VENDOR_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {PRICE_VARIANT_CITIES_SLUGS} from "@pg-rp/navigation";

import {IRPStore} from "../../../../../app/rp_reducer";
import {ColumnList} from "../../../../../atoms/ColumnList";
import {ColumnListItem} from "../../../../../atoms/ColumnListItem";
import {InfoPanel} from "../../../../../atoms/InfoPanel";
import {Country} from "../../../../../region/types/Country";
import {useSearchForm} from "../../../../../search/hooks/use_search_form";
import {SearchFormValuesType} from "../../../../../search/types/SearchFormValuesType";
import {TdaPortal} from "../../../../../smart_ad_server/components/TdaPortal";
import {SmartAdServerPlacement} from "../../../../../smart_ad_server/SmartAdServerPlacement";
import {offersVisibleHit} from "../../../../../tracking/algolytics_hits/offers_visible_hit";
import {trackAmplitudeButtonClick} from "../../../../../tracking/amplitude/track_amplitude_button_click";
import {OfferBoxWithOfferDetailsAction} from "../../../../atoms/OfferBoxWithOfferDetailsAction";
import {BudgetFilterBox} from "../../../../components/BudgetFilterBox";
import {BudgetVariantRadioBox} from "../../../../components/BudgetVariantRadioBox";
import {RoomsFilterBox} from "../../../../components/RoomsFilterBox";
import {getAvailableFlatPriceVariants} from "../../../../helpers/get_flat_price_variant_links";
import {SmartAdSASTypes} from "../../../../helpers/smart_add_sas_types";
import {useOffersViewsUrl} from "../../../../hooks/use_offers_views_url";
import {hitGTMOfferViewItemList} from "../../../../tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../../../../tracking/offer_box_gtm_events";
import {IOfferListOffer} from "../../../../types/IOfferListOffer";
import {OfferType} from "../../../../types/OfferType";
import {getOfferCardAddress, IOfferCardAddressContext} from "../../../../utils/get_offer_card_address";
import {ISelectedRegion} from "../../../actions/offer_list_selected_data_actions";
import {OFFER_LIST_PAGE_SIZE} from "../../../constants/offer_list";
import {getListingLayoutSearchKey} from "../../../helpers/get_listing_layout_search_key";
import {getOfferListingAmplitudeItemListContext} from "../../../helpers/get_offer_listing_amplitude_item_list_context";
import {isPromotionsListingQuery} from "../../../helpers/map_offer_list_promotions_query_param";
import {shouldShowOfferListingPromotionTabs} from "../../../helpers/should_show_offer_listing_promotion_tabs";
import {getCountryId} from "../../../helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {useGetOfferListingAllOffers} from "../../../hooks/use_get_offer_listing_all_offers";
import {disableDynamicPositionProps, useListDynamicPositions} from "../../../hooks/use_list_dynamic_positions";
import {useOfferListingAmplitudeViewItemListTracking} from "../../../hooks/use_offer_listing_amplitude_view_item_list_tracking";
import {usePageLinkBuilder} from "../../../hooks/usePageLinkBuilder";
import {IOfferListListStore} from "../../../reducers/desktop_offer_list_list_reducer";
import {reservedGridRowStyle} from "../../../styles/reserved_grid_row_style";
import {OfferListSubType} from "../../../types/OfferListSubType";
import {hasPriceFilter} from "../../../utils/has_price_filter";
import {DefaultOfferListingModal} from "../../DefaultOfferListingModal";
import {NextPageBox} from "../../NextPageBox";
import {OfferListingPromotionTabs} from "../../OfferListingPromotionTabs";
import {OfferListSort} from "../../OfferListSort";
import {GetHomeLinkingBox} from "../GetHomeLinkingBox";
import {NotificationsListBox} from "../NotificationsListBox";
import {OfferListHeader} from "../OfferListHeader";
import {OfferListMidBanner} from "../OfferListMidBanner";
import {PromotionsListingTile} from "../PromotionsListingTile";
import {UnactiveOfferList} from "./UnactiveOfferList";

interface IProps {
    list: IOfferListListStore;
    metaDataTitle: string;
    selectedRegion: ISelectedRegion[];
    offerListSubType?: OfferListSubType;
    isSidebarReserved?: boolean;
    isInitialOfferListFetchDone: boolean;
    addressContext: IOfferCardAddressContext;
}

export enum CreationListingPosition {
    SURPRISE_LONG = 17,
    SURPRISE_SHORT = 10
}

export const DefaultOfferListing = (props: IProps) => {
    const {addressContext, list, metaDataTitle, selectedRegion, offerListSubType, isSidebarReserved, isInitialOfferListFetchDone} = props;
    const {
        count,
        offers,
        privileged_offers,
        page,
        unavailable_offers,
        unavailable_offers_count,
        requestState,
        latestQuery,
        page_size,
        allOffers,
        propertiesCount,
        listingContextPromotionalOffersCount,
        listingContextPromotionalOffersCountRequestState
    } = list;
    const searchFormValues = useSelector((state: IRPStore) => state.search.formValues) as SearchFormValuesType;
    const standardMetaData = useSelector((state: IRPStore) => state.metaData.standardMetaData);
    const searchFormRooms = searchFormValues.rooms as IApiRange<number | "">;
    const priceFilterUsed = hasPriceFilter(searchFormValues);
    const allOffersCount = priceFilterUsed ? allOffers.filter((offer) => offer.has_required_prices === true).length : count;
    const buildPageLink = usePageLinkBuilder();
    const {isMobile} = useUserDevice();
    const location = useLocation();
    const routeQuery = parseSearch(location.search);
    /**
     * `location.search` also carries the routed-modal param, which changes on every "Zobacz mieszkania"
     * click without changing what the listing renders. Keying the render memo off the stripped search
     * keeps opening the modal from re-rendering the whole list
     */
    const layoutSearchKey = getListingLayoutSearchKey(location.search);
    // built once instead of per tile, so tiles without required prices keep a stable prop identity
    const latestQueryWithoutPrice = useMemo(() => omit(latestQuery, ["price_0", "price_1", "instalment"]), [latestQuery]);
    const prevRequestState = usePrevious(requestState, RequestState.None);
    const offerType = !!latestQuery.type ? parseInt(latestQuery.type) : undefined;
    const currentCountry = getCountryId(latestQuery.country);
    const isAbroadListing = currentCountry && currentCountry !== Country.POLAND;
    const listingPageSize = page_size || OFFER_LIST_PAGE_SIZE;
    const lastPage = allOffersCount ? Math.ceil(allOffersCount / listingPageSize) : 1;
    const isPromotionsTab = isPromotionsListingQuery(routeQuery);
    const showPromotionTabs = shouldShowOfferListingPromotionTabs({
        latestQuery,
        routeQuery,
        listingContextPromotionalOffersCount,
        listingContextPromotionalOffersCountRequestState
    });
    const listingListKey = isPromotionsTab ? "promotions" : "all";

    const offerRequestReady = prevRequestState === RequestState.Waiting && requestState === RequestState.Success;
    const isLastPage = page === lastPage;
    const lastCombinedPage = Math.ceil((count + unavailable_offers_count) / listingPageSize) || 1;
    // latestQuery.page is the URL page (set on fetchOfferListTypes.start, i.e. immediately on navigation).
    // For pure-unavailable pages the store's `page` field holds activeLastPage (not the URL page),
    // so comparing against that would always miss the last combined page.
    const urlPage = latestQuery.page ? parseInt(String(latestQuery.page), 10) : 1;
    const isLastCombinedPage = urlPage === lastCombinedPage;
    // True for transition and pure-unavailable pages — use stable computed values, not unavailable_offers.length
    // (which can be transiently stale from a previous combined page during loading)
    const activeLastPageFromCount = count > 0 ? Math.ceil(count / listingPageSize) : 1;
    const isOnCombinedOrUnavailablePage = page >= activeLastPageFromCount && unavailable_offers_count > 0;
    const unavailableOffersIds = unavailable_offers.map((offer) => offer.id).join(",");

    const {mainOffers, additionalOffers, mainOffersCount} = useGetOfferListingAllOffers({
        offersOnPage: offers,
        allOffers,
        latestQuery,
        searchFormValues,
        privilegedOffers: privileged_offers,
        isLastPage
    });

    const offersToRender = [...mainOffers, ...additionalOffers];

    const shouldShowRoomsFilterBox = mainOffers.length !== 0 && count >= 24 && searchFormRooms.upper === "" && searchFormRooms.lower === "";
    const shouldShowBudgetFilterBox = mainOffers.length !== 0 && count >= 24 && !searchFormValues.instalment && !searchFormValues.price.upper;

    const seenOffers = useRef<number[]>([]);
    const roomsFilterBoxPosition = 4;
    const budgetFilterBoxPosition = shouldShowRoomsFilterBox ? roomsFilterBoxPosition + 3 : roomsFilterBoxPosition;
    const priceDividerOfferIndex = offers.findIndex((el) => el.has_required_prices !== true) || 0;
    const dividedOffersCount = priceFilterUsed ? offers.slice(priceDividerOfferIndex, offers.length).length : 0;

    const {mapUrl} = useOffersViewsUrl();

    const debouncedOffersVisibleHit = debounce(() => {
        offersVisibleHit(seenOffers.current);

        seenOffers.current = [];
    }, 500);

    const offersToRenderIds = useMemo(() => [...offersToRender.map(({id}) => id.toString())], [offersToRender]);
    const offerToRenderIndexes = useMemo(() => {
        const indexes: {[key: string]: number} = {};
        offersToRender.forEach((offer, index) => {
            indexes[offer.id] = index;
        });
        return indexes;
    }, [offersToRender]);

    useElementWasVisible({
        ids: offersToRenderIds,
        indexes: offerToRenderIndexes,
        onElementVisible: (id, _, index) => {
            seenOffers.current = [...seenOffers.current, parseInt(id)];
            index !== undefined && offerBoxTracking.gtm.investTileView(parseInt(id), index);
            debouncedOffersVisibleHit();
        },
        once: true
    });

    const offersListLayoutKey = useMemo(
        () => `${listingListKey}|${layoutSearchKey}|${offersToRender.map((offer, index) => `${offer.id}-${index}`).join(",")}`,
        [listingListKey, layoutSearchKey, offersToRender]
    );

    const {wrapperProps, initializeDynamicPosition, getDynamicPositionProps} = useListDynamicPositions<HTMLDivElement>([offersListLayoutKey]);
    const {
        wrapperProps: unavailableWrapperProps,
        initializeDynamicPosition: initializeUnavailableDynamicPosition,
        getDynamicPositionProps: getUnavailableDynamicPositionProps
    } = useListDynamicPositions<HTMLDivElement>([unavailableOffersIds]);

    // Commented out due to RP-217 requirements
    // const midListingReels = useMemo(() => {
    //     return <ListingReels selectedRegions={props.selectedRegion} className="listing-reels" />;
    // }, [props.selectedRegion]);

    const {openLocationSearch} = useSearchForm();
    const handleHeaderClick = () => {
        openLocationSearch();
    };

    const prevMetaDataTitle = useRef<string | null>(null);

    const listingTitle = metaDataTitle.split(" - ")[0];
    const amplitudeItemListContext = useMemo(
        () =>
            getOfferListingAmplitudeItemListContext({
                listingTitle,
                isPromotionsTab,
                hasPromotionTabs: showPromotionTabs,
                latestQuery
            }),
        [listingTitle, isPromotionsTab, showPromotionTabs, latestQuery]
    );

    useOfferListingAmplitudeViewItemListTracking({
        offers: offersToRender,
        amplitudeItemListContext,
        routeQuery,
        latestQuery,
        requestState,
        isInitialOfferListFetchDone,
        metaDataRevision: standardMetaData
    });

    const handleMapButtonClick = () => {
        trackAmplitudeButtonClick({
            buttonName: "view_map",
            buttonCategory: "listing_page",
            value: "see_on_map",
            itemId: null,
            buttonLink: null,
            itemListId: amplitudeItemListContext.itemListId
        });
    };

    // memoized so that the memo() on OfferBox actually holds - a fresh object here re-rendered every tile
    const trackingMeta = useMemo(
        () => ({
            listId: "listing",
            listName: listingTitle,
            source: "listing" as const,
            tileEvents: {
                imageClick: offerBoxTracking.gtm.investTileImageClick,
                buttonClick: offerBoxTracking.gtm.investTileButtonClick,
                linkClick: offerBoxTracking.gtm.investTileLinkClick,
                logoClick: offerBoxTracking.gtm.investTileLogoClick
            }
        }),
        [listingTitle]
    );

    useEffect(() => {
        if (requestState === RequestState.Success && prevMetaDataTitle.current !== metaDataTitle) {
            prevMetaDataTitle.current = metaDataTitle;
            hitGTMOfferViewItemList({
                offers: offersToRender,
                listId: trackingMeta.listId,
                listName: trackingMeta.listName
            });
        }
    }, [requestState, offersToRenderIds.join(","), metaDataTitle]);

    const renderListSection = useMemo(() => {
        let hasPriceDivider = false;

        const renderDivider = (has_required_prices: boolean | null) => {
            const shouldHaveDivider = !hasPriceDivider && has_required_prices === false;
            if (shouldHaveDivider) {
                hasPriceDivider = true;
            }
            return (
                shouldHaveDivider && (
                    <Col sm={4} md={12} lg={16} className={dividerStyles}>
                        <InfoPanel
                            icon={BrandSearchIcon}
                            heading="Znaleźliśmy także inne oferty, które mogą nie spełniać Twoich oczekiwań cenowych."
                            subHeading="Szczegółowych informacji udzielają deweloperzy"
                        />
                    </Col>
                )
            );
        };

        const getRegion = () => {
            const regionId = latestQuery.region && latestQuery.region?.length > 0 ? +latestQuery.region[latestQuery.region.length - 1] : undefined;
            const selectedRegionData = selectedRegion.find((region) => region.id === regionId);

            if (regionId && selectedRegion.length > 0) {
                return {
                    id: regionId,
                    name: selectedRegionData?.name,
                    slug: selectedRegionData?.slug,
                    parentSlug: selectedRegionData?.parent?.slug,
                    type: selectedRegionData?.type,
                    stats: selectedRegionData?.stats
                };
            }
            return {
                id: regionId,
                name: undefined,
                slug: undefined,
                parentSlug: undefined,
                type: undefined,
                stats: undefined
            };
        };
        const region = getRegion();

        const budgetPriceVariants =
            offerType === OfferType.FLAT && !!region.slug && PRICE_VARIANT_CITIES_SLUGS.includes(region.slug)
                ? getAvailableFlatPriceVariants(region.stats)
                : [];
        const showBudgetVariants = budgetPriceVariants.length >= 3;

        let suffix = 1;
        initializeDynamicPosition();
        initializeUnavailableDynamicPosition();
        return (
            <>
                {!isMobile && (!currentCountry || currentCountry === Country.POLAND) && (
                    <TdaPortal>
                        <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />
                    </TdaPortal>
                )}
                <DefaultOfferListingModal />

                <OfferListHeader
                    offerCount={allOffers.length > 0 ? mainOffersCount : count - dividedOffersCount}
                    propertiesCount={propertiesCount}
                    selectedRegion={selectedRegion}
                    latestQuery={latestQuery}
                    offerListSubType={offerListSubType}
                    onHeaderClick={handleHeaderClick}
                />

                {/*<WideContent css={[display("none"), onDesktop(display("block"), mb(4), w100)]}>*/}
                {/*    <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} fetchPriority="high" preventGtmOnMobile />*/}
                {/*</WideContent>*/}

                <WideContent>
                    <div className={cx(offerListControlsStyle, showPromotionTabs ? offerListControlsSpaceBetweenStyle : offerListControlsFlexEndStyle)}>
                        {/* @deprecated Comment code releated with displaying "Mieszkania i domy" tab due to RP-217 requirements
                        this can be delted entirely in future if we will keep this approach */}
                        {/* <ListingTypeTabs
                            filters={searchFormValues}
                            offerType={offerType}
                            currentListingType={ListingType.OFFERS}
                            additionalParams={{sort: props.list.latestQuery.sort}}
                        /> */}

                        {showPromotionTabs && <OfferListingPromotionTabs amplitudeItemListContext={amplitudeItemListContext} />}

                        <div className={mapButtonsWrapperStyle}>
                            <Button
                                className={mapButtonStyle}
                                variant="filled_secondary"
                                size="small"
                                href={mapUrl}
                                iconLeft={MapIcon}
                                onClick={handleMapButtonClick}
                            >
                                zobacz na mapie
                            </Button>

                            <OfferListSort type="select" name="offer-sort" latestQuerySort={latestQuery.sort} css={offerListSortWrapperStyle} />
                        </div>
                    </div>
                </WideContent>
                <ColumnList
                    {...wrapperProps}
                    className={isSidebarReserved ? listDynamicRowOrderReservedSidebarStyle : listDynamicRowOrderStyle}
                    rowClassName={isSidebarReserved ? reservedGridRowStyle : undefined}
                >
                    {offersToRender.map((offer: IOfferListOffer, idx: number) => {
                        const position = idx + 1;
                        const isShortListCase = offersToRender.length < 3 && position === offersToRender.length; // list is short, so detect the last element
                        const isFifthLastIndex = position === offersToRender.length - 5;
                        const isLastPosition = position === offersToRender.length;

                        const surpriseAdPosition =
                            offersToRender.length >= CreationListingPosition.SURPRISE_LONG
                                ? CreationListingPosition.SURPRISE_LONG
                                : CreationListingPosition.SURPRISE_SHORT;

                        return (
                            <React.Fragment key={`${offer.id}-${idx}`}>
                                {renderDivider(offer.has_required_prices)}
                                <ColumnListItem data-testid={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT} dynamicProps={getDynamicPositionProps()}>
                                    <OfferBoxWithOfferDetailsAction
                                        offer={offer}
                                        latestQuery={offer.has_required_prices === false ? latestQueryWithoutPrice : latestQuery}
                                        index={idx}
                                        trackingMeta={trackingMeta}
                                        displayAddress={getOfferCardAddress(offer, addressContext)}
                                        amplitudeItemListContext={amplitudeItemListContext}
                                        dataTestId={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}
                                    />
                                </ColumnListItem>
                                {/*{position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.HERO} />}*/}
                                {/* Hidden due to RP-217 requirements */}
                                {/* {position === 2 && midListingReels} */}
                                {position === 2 && isMobile && <SmartAdServerPlacement adFormat={SmartAdSASTypes.TDA} />}
                                {(isShortListCase || position === 3) && <SmartAdServerPlacement adFormat={SmartAdSASTypes.OFFER_OF_THE_MONTH} />}
                                {(isShortListCase || position === 3 || position === 7 || position === 11) && (
                                    <SmartAdServerPlacement adFormat={SmartAdSASTypes.MONO_NATIVE_ADD} suffix={suffix++} />
                                )}

                                {isFifthLastIndex && <SmartAdServerPlacement adFormat={SmartAdSASTypes.IMAGE} />}
                                {position === surpriseAdPosition && (
                                    <SmartAdServerPlacement
                                        adFormat={SmartAdSASTypes.SURPRISE}
                                        referenceElementId={LISTING_VENDOR_TEST_IDS.FIRST_LISTING_ELEMENT}
                                    />
                                )}
                                {position === 5 && <OfferListMidBanner />}

                                {position === 6 && (
                                    <PromotionsListingTile
                                        selectedRegion={selectedRegion}
                                        getDynamicPositionProps={getDynamicPositionProps}
                                        listingPage={page}
                                        tilePosition={position}
                                        amplitudeItemListContext={amplitudeItemListContext}
                                    />
                                )}

                                {isLastPosition && !isOnCombinedOrUnavailablePage && (
                                    <>
                                        <ColumnListItem {...getDynamicPositionProps()}>
                                            <NextPageBox
                                                isLastPage={isLastCombinedPage}
                                                offerTypes={offerType ? [offerType] : [OfferType.FLAT, OfferType.HOUSE]}
                                                nextPageLink={buildPageLink(page + 1)}
                                            />
                                        </ColumnListItem>
                                        {isLastCombinedPage && region.name && (
                                            <ColumnListItem {...getDynamicPositionProps()}>
                                                <NotificationsListBox regionName={region.name} />
                                            </ColumnListItem>
                                        )}
                                        {isLastCombinedPage && offerType !== OfferType.COMMERCIAL && !isAbroadListing && (
                                            <ColumnListItem {...getDynamicPositionProps()}>
                                                <GetHomeLinkingBox offerType={offerType} region={region} />
                                            </ColumnListItem>
                                        )}
                                    </>
                                )}
                                {shouldShowRoomsFilterBox && position === roomsFilterBoxPosition && (
                                    <ColumnListItem
                                        dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}
                                        className="rp-filters-tile"
                                    >
                                        <RoomsFilterBox />
                                    </ColumnListItem>
                                )}
                                {shouldShowBudgetFilterBox && position === (isMobile ? budgetFilterBoxPosition - 1 : budgetFilterBoxPosition) && (
                                    <ColumnListItem dynamicProps={isMobile ? disableDynamicPositionProps() : getDynamicPositionProps()}>
                                        {showBudgetVariants ? <BudgetVariantRadioBox priceVariants={budgetPriceVariants} /> : <BudgetFilterBox />}
                                    </ColumnListItem>
                                )}
                            </React.Fragment>
                        );
                    })}
                </ColumnList>

                {unavailable_offers.length > 0 && (
                    <>
                        <WideContent>
                            <Text variant="mini_header" as="p" css={[offersToRender.length > 0 && mt(6), mb(3)]}>
                                Oferty nieaktywne
                            </Text>
                        </WideContent>
                        <ColumnList
                            {...unavailableWrapperProps}
                            className={isSidebarReserved ? listDynamicRowOrderReservedSidebarStyle : listDynamicRowOrderStyle}
                            rowClassName={isSidebarReserved ? reservedGridRowStyle : undefined}
                        >
                            <UnactiveOfferList offers={unavailable_offers} addressContext={addressContext} />

                            <ColumnListItem {...getUnavailableDynamicPositionProps()}>
                                <NextPageBox
                                    isLastPage={isLastCombinedPage}
                                    offerTypes={offerType ? [offerType] : [OfferType.FLAT, OfferType.HOUSE]}
                                    nextPageLink={buildPageLink(page + 1)}
                                />
                            </ColumnListItem>
                            {isLastCombinedPage && region.name && (
                                <ColumnListItem {...getUnavailableDynamicPositionProps()}>
                                    <NotificationsListBox regionName={region.name} />
                                </ColumnListItem>
                            )}
                            {isLastCombinedPage && offerType !== OfferType.COMMERCIAL && !isAbroadListing && (
                                <ColumnListItem {...getUnavailableDynamicPositionProps()}>
                                    <GetHomeLinkingBox offerType={offerType} region={region} />
                                </ColumnListItem>
                            )}
                        </ColumnList>
                    </>
                )}
            </>
        );
    }, [
        offersToRenderIds,
        offerRequestReady,
        metaDataTitle,
        latestQuery,
        allOffers.length,
        listingListKey,
        layoutSearchKey,
        showPromotionTabs,
        amplitudeItemListContext,
        isSidebarReserved,
        unavailable_offers_count,
        unavailableOffersIds,
        isLastCombinedPage,
        isOnCombinedOrUnavailablePage,
        addressContext
    ]);

    return renderListSection;
};

//  Styles
const dividerStyles = css({
    py: "3rem",
    px: 0
});

const offerListControlsStyle = css({
    display: "flex",
    alignItems: "center",
    pb: 2,
    mt: 2,
    md: {
        pb: 3,
        mt: 0
    },
    "& > div": {
        pb: 0
    }
});

const offerListControlsSpaceBetweenStyle = css({
    justifyContent: "space-between"
});

const offerListControlsFlexEndStyle = css({
    justifyContent: "flex-end"
});

const mapButtonsWrapperStyle = css({
    display: "flex",
    gap: "1rem",
    alignItems: "center"
});

const mapButtonStyle = css({
    display: "none",
    md: {
        display: "inherit"
    }
});

const offerListSortWrapperStyle = emotionCss`
    display: none;
    padding-bottom: 0;

    ${onDesktop(emotionCss`
        display: block;
        flex: 0 0 16rem;
    `)};
`;

const listDynamicRowOrderRawStyle = css.raw({
    mdDown: {
        '& [data-pos="0"], & [data-pos="1"]': {
            order: -4
        },
        "& .listing-reels": {
            order: -3
        },
        '& [data-pos="2"], & [data-pos="3"]': {
            order: -2
        },
        "& .rp-filters-tile": {
            order: -1
        }
    },
    md: {
        '& [data-pos="0"], & [data-pos="1"], & [data-pos="2"], & [data-pos="3"], & [data-pos="4"], & [data-pos="5"]': {
            order: -4
        },
        "& .listing-reels": {
            order: -3
        },
        '& [data-pos="6"], & [data-pos="7"], & [data-pos="8"], & [data-pos="9"], & [data-pos="10"], & [data-pos="11"]': {
            order: -2
        },
        "& .listing-banner": {
            order: -1
        }
    }
});

const listDynamicRowOrderReservedSidebarStyle = css({
    ...listDynamicRowOrderRawStyle
});

const listDynamicRowOrderStyle = css({
    ...listDynamicRowOrderRawStyle,
    lg: {
        '& [data-pos="0"], & [data-pos="1"], & [data-pos="2"], & [data-pos="3"], & [data-pos="4"], & [data-pos="5"], & [data-pos="6"], & [data-pos="7"]': {
            order: -4
        },
        '& [data-pos="8"], & [data-pos="9"], & [data-pos="10"], & [data-pos="11"], & [data-pos="12"], & [data-pos="13"], & [data-pos="14"], & [data-pos="15"]':
            {
                order: -2
            }
    }
});
